import React, { ChangeEvent, FocusEvent, useState } from 'react';
import {
  FormControl,
  Button,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  SelectChangeEvent,
  Box,
  OutlinedInput,
  Chip,
  Checkbox,
  ListItemText
} from '@mui/material';
import styles from '../../../index.module.scss';
import BaseModal from '../../shared/components/base.modal';
import apiClient from '../../shared/auth.interceptor';
import { errorToast } from '../../shared/toast.service';
import { UserRoles } from './useFetchUsers.hook';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { IFirm } from '../../shared/redux/firmSlicer';
import { syncFirms } from '../../shared/redux/syncFirms';
import { firmRefresh, getUserRole } from '../../shared/auth.functions';
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined';

interface AddNewUserModalProps {
  open: boolean;
  edit: boolean;
  handleClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  role: UserRoles | '';
  companies: string[]; // realmId[]
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  companies?: string;
}

const INITIAL_STATE: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  role: UserRoles.simple_user,
  companies: []
};

const AddNewUserModal: React.FC<AddNewUserModalProps> = ({ open, handleClose, onSuccess }) => {
  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);
  const [formData, setFormData] = useState<FormData>(INITIAL_STATE);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<{ [key in keyof FormData]?: boolean }>({});
  const currentFirm = useSelector((state: RootState) => state.currentFirm.currentFirm);

  // список компаний берём из стора
  const allCompanies = useSelector((state: RootState) => state.companyList.companyList);
  // === Field validation ===
  const getFieldError = (name: keyof FormData, value: string | string[]): string | undefined => {
    let error: string | undefined;

    switch (name) {
      case 'firstName':
      case 'lastName': {
        const v = String(value);
        if (!v.trim()) error = 'Please enter this field';
        else if (v.length < 2) error = 'Minimum 2 letters';
        else if (v.length > 40) error = 'Name is too long';
        break;
      }
      case 'email': {
        const v = String(value);
        if (!v.trim()) error = 'Please enter email';
        else if (!/^[\w\-.]+@[\w\-.]+\.[A-Za-z]{2,}$/.test(v)) error = 'Invalid email';
        break;
      }
      case 'role': {
        const v = String(value);
        if (!v) error = 'Please select a role';
        break;
      }
      case 'companies': {
        if (formData.role === UserRoles.simple_user) {
          const arr = value as string[];
          if (!arr.length) error = 'Please select at least one company';
          else if (arr.some(id => !allCompanies.some(company => company.realmId === id))) {
            error = 'Please select clients from the current firm';
          }
        }
        break;
      }
      default:
        break;
    }

    return error;
  };

  const validateField = (name: keyof FormData, value: string | string[]) => {
    const error = getFieldError(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateAll = (): boolean => {
    const currentErrors: FormErrors = {};

    (['firstName', 'lastName', 'email', 'role', 'companies'] as const).forEach(key => {
      const error = getFieldError(key, formData[key]);
      if (error) currentErrors[key] = error;
    });

    setErrors(currentErrors);
    return Object.values(currentErrors).every((e) => !e);
  };

  // === Handle changes ===
  const handleInputChange = (event: ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name as string]: value as string }));
    validateField(name as keyof FormData, value as string);
  };

  const handleSelectChange = (event: SelectChangeEvent<UserRoles>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name as string]: value as UserRoles,
      companies: value === UserRoles.super_admin ? [] : prev.companies
    }));
    validateField(name as keyof FormData, value as string);
    if (value !== UserRoles.simple_user) {
      setErrors((prev) => ({ ...prev, companies: undefined }));
    }
  };

  const handleCompaniesChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    setFormData((prev) => ({ ...prev, companies: value }));
    setTouched((prev) => ({ ...prev, companies: true }));
    validateField('companies', value);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name as keyof FormData, value);
  };

  // === Handle form submit ===
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAll()) {
      setTouched({
        firstName: true,
        lastName: true,
        email: true,
        role: true,
        companies: true
      });
      return;
    }

    if (!currentFirm?._id) {
      errorToast('Please select a firm');
      return;
    }

    try {
      const createRes = await apiClient.post('/v1/user/invite', {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
      });

      const createdUserId: string | undefined = createRes?.data?._id ?? createRes?.data?.id;
      if (!createdUserId) {
        throw new Error('Cannot get created user id');
      }
      switch (formData.role) {
        case UserRoles.admin: {
          await apiClient.post<IFirm>(`/v1/firm/assign-firm/${currentFirm?._id}`, {
            userId: createdUserId
          });
          dispatch(syncFirms(await firmRefresh()));
          break;
        }
        case UserRoles.simple_user: {
          await apiClient.post<IFirm>(`/v1/firm/assign-clients/${currentFirm._id}`, {
            userId: createdUserId,
            companyIds: formData.companies
          });
          dispatch(syncFirms(await firmRefresh()));
          break;
        }
      }

      setFormData(INITIAL_STATE);
      setTouched({});
      setErrors({});
      onSuccess();
      handleClose();
    } catch (error: any) {
      errorToast(error?.response?.data?.message ?? 'Error creating user');
    }
  };

  const handleModalClose = () => {
    setFormData(INITIAL_STATE);
    setTouched({});
    setErrors({});
    handleClose();
  };

  const isSuperAdmin = formData.role === UserRoles.super_admin;

  return (
    <BaseModal open={open} handleClose={handleModalClose}>
      <img alt="Logo" src="/logo120.svg" />
      <Typography variant="h6" sx={{ mb: 2 }}>
        Enter the Following User Data:
      </Typography>

      <form onSubmit={handleSubmit} autoComplete="off">
        <FormControl fullWidth margin="normal">
          <TextField
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            onBlur={handleBlur}
            error={Boolean(errors.firstName && touched.firstName)}
            helperText={touched.firstName && errors.firstName}
            variant="outlined"
            required
            inputProps={{ maxLength: 40 }}
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <TextField
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            onBlur={handleBlur}
            error={Boolean(errors.lastName && touched.lastName)}
            helperText={touched.lastName && errors.lastName}
            variant="outlined"
            required
            inputProps={{ maxLength: 40 }}
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <TextField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            onBlur={handleBlur}
            error={Boolean(errors.email && touched.email)}
            helperText={touched.email && errors.email}
            variant="outlined"
            required
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel id="role-label">Role</InputLabel>
          <Select
            labelId="role-label"
            name="role"
            sx={{ textAlign: 'left' }}
            value={formData.role}
            onChange={handleSelectChange}
            onBlur={() => setTouched((prev) => ({ ...prev, role: true }))}
            label="Role"
            error={Boolean(errors.role && touched.role)}
            required
          >
            {getUserRole(currentUser, currentFirm) === UserRoles.super_admin && [
              // <MenuItem key="super_admin" value={UserRoles.super_admin}>
              //   Super Admin
              // </MenuItem>,
              <MenuItem key="admin" value={UserRoles.admin}>
                Admin
              </MenuItem>
            ]}
            <MenuItem value={UserRoles.simple_user}>User</MenuItem>
          </Select>
          {touched.role && errors.role && (
            <Typography color="error" variant="caption">
              {errors.role}
            </Typography>
          )}
        </FormControl>

        {!isSuperAdmin &&
          (formData?.role === UserRoles.simple_user ? (
            <FormControl fullWidth margin="normal">
              <InputLabel id="companies-label">Clients</InputLabel>
              <Select
                labelId="companies-label"
                multiple
                name="companies"
                sx={{ height: 'auto !important', textAlign: 'left' }}
                value={formData.companies}
                onChange={handleCompaniesChange}
                input={<OutlinedInput id="select-multiple-chip" label="Clients" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={allCompanies.find(company => company.realmId === value)?.companyName || value} sx={{ m: 0.5, maxWidth: 250 }} />
                    ))}
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 48 * 4.5 + 8,
                      width: 300
                    }
                  }
                }}
              >
                {allCompanies.length ? (
                  allCompanies.map((c: any) => {
                    const selected = formData.companies.indexOf(c.realmId) > -1;
                    return (
                      <MenuItem key={c.realmId} value={c.realmId}>
                        <Checkbox color="info" size="small" checked={selected} />
                        <ListItemText primary={c.companyName} />
                      </MenuItem>
                    );
                  })
                ) : (
                  <MenuItem value="" disabled>
                    No companies available
                  </MenuItem>
                )}
              </Select>
              {touched.companies && errors.companies && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                  {errors.companies}
                </Typography>
              )}
            </FormControl>
          ) : (
            <FormControl fullWidth margin="normal">
              <InputLabel id="firm-select-label">Firm</InputLabel>
              <Select
                labelId="firm-select-label"
                sx={{ height: 'auto !important', textAlign: 'left' }}
                value={currentFirm?._id ?? ''}
                disabled
              >
                <MenuItem value={currentFirm?._id ?? ''}>{currentFirm?.name ?? '—'}</MenuItem>
              </Select>
            </FormControl>
          ))}

        <Button
          type="submit"
          className="contained"
          startIcon={<ControlPointOutlinedIcon />}
          sx={{ mt: 2, width: '100%', bgcolor: styles.mainBlue, color: 'white' }}
        >
          Invite New User
        </Button>
        {/* Для отправки формы можно вернуть type="submit" у кнопки выше */}
      </form>
    </BaseModal>
  );
};

export default AddNewUserModal;
